import type { CreateSourceRequest } from '@/types/requests/sources/createSourceRequest';
import type { UpdateSourceRequest } from '@/types/requests/sources/updateSourceRequest';
import type { Source } from '@/types/source';
import { callApi } from '@/utils/api';
import { handleAxiosError } from '@/utils/error';
import toast from '@/utils/toast';
import { AxiosError } from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useSourceStore = defineStore('sources', () => {
    /**
     * The combined list of global and organization sources.
     */
    const allSources = computed(() => [...organizationSources.value, ...globalSources.value].sort((a, b) => a.name.localeCompare(b.name)));

    const globalSources = ref<Source[]>([]);
    const organizationSources = ref<Source[]>([]);

    async function getByOrganizationId(organizationId: string) {
        try {
            organizationSources.value = await callApi<Source[]>({ method: 'GET', endpoint: `sources/${organizationId}` });
            return organizationSources.value;
        } catch (error) {
            return handleAxiosError(error, [], 'Bronnen konden niet worden opgehaald');
        }
    }

    async function getGlobalSources() {
        try {
            globalSources.value = await callApi<Source[]>({ method: 'GET', endpoint: 'sources/global' });
            return globalSources.value;
        } catch (error) {
            return handleAxiosError(error, [], 'Bronnen konden niet worden opgehaald');
        }
    }

    async function create(request: CreateSourceRequest) {
        try {
            await callApi({ method: 'POST', endpoint: 'sources', body: request });

            if (request.organization) await getByOrganizationId(request.organization);
            else await getGlobalSources();

            toast.success('SourceSaved');
        } catch (error) {
            if (handleAlreadyExists(error)) return;
            handleAxiosError(error, [], 'Bron kon niet aangemaakt worden.');
        }
    }

    async function update(request: UpdateSourceRequest) {
        try {
            await callApi({ method: 'PUT', endpoint: 'sources', body: request });

            if (request.organization) {
                organizationSources.value = organizationSources.value.map((source) =>
                    source._id === request._id ? { ...source, ...request } : source
                );
            } else {
                globalSources.value = globalSources.value.map((source) => (source._id === request._id ? { ...source, ...request } : source));
            }

            toast.success('SourceSaved');
        } catch (error) {
            if (handleAlreadyExists(error)) return;
            handleAxiosError(error, [], 'Bron kon niet bijgewerkt worden.');
        }
    }

    function handleAlreadyExists(error: unknown) {
        if (error instanceof AxiosError && error.response?.status === 409) {
            toast.error('SourceAlreadyExists');
            return true;
        }
    }

    async function remove(id: string) {
        try {
            await callApi({ method: 'DELETE', endpoint: `sources/${id}` });
            globalSources.value = globalSources.value.filter((source) => source._id !== id);
            organizationSources.value = organizationSources.value.filter((source) => source._id !== id);
            toast.success('SourceDeleted');
        } catch (error) {
            handleAxiosError(error, [], 'Bron kon niet verwijderd worden.');
        }
    }

    return {
        globalSources,
        organizationSources,
        allSources,
        getByOrganizationId,
        getGlobalSources,
        create,
        update,
        remove
    };
});
